// layoutSwitchAnalytics — fire-and-forget instrumentation for Classic ⇄ Modern
// layout switches (pen-usage-analytics-2026-08-18.md §9).
//
// Mirrors `timelapseShareAnalytics.ts` (same Transport-port-only, best-effort
// send). The one payload field (`layout`) is the value **after** the switch —
// direction and switch count are reconstructed server-side from the sequence
// of pings within a session (protocol/src/events/analytics.ts).
//
// Caller discipline (pen-usage-analytics-2026-08-18.md §9.3 / protocol
// comment on `analytics.layout-switch`): only call this from the **explicit
// toggle** moment — the same call site that already persists `LAYOUT_KEY` to
// localStorage (`useLayoutPreference.setLayout`). Do NOT call it:
//   - on every render (this is an event, not a state sync)
//   - from `layoutResolution.ts`'s mobile-forced-Modern resolution — that is
//     NOT a user choice, and counting it would fabricate "every mobile user
//     switched Classic → Modern" in the data.

import type { LayoutKind } from '@oekaki/protocol';
import type { Transport } from './ports/Transport.js';

/**
 * Report a Classic ⇄ Modern layout switch. Fire-and-forget: silently no-ops
 * when the socket is not open (= not in a room yet, e.g. the pre-join
 * `ReJoinScreen` layout choice — nothing to attribute) and swallows any send
 * error — instrumentation must never interfere with the layout toggle UX.
 */
export const reportLayoutSwitch = (transport: Transport, layout: LayoutKind): void => {
  if (transport.status.t !== 'open') return;
  try {
    transport.send({ t: 'analytics.layout-switch', layout });
  } catch {
    // send-before-open / serialisation quirk — analytics is best-effort.
  }
};
